import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import {
  Envelope, Phone, Buildings, Clock, Trash, ArrowsClockwise,
  CheckCircle, Handshake, Archive, X,
} from "@phosphor-icons/react";

const STATUS_META = {
  new:       { label: "New",       tone: "bg-[#dbeafe] text-[#1e40af]" },
  contacted: { label: "Contacted", tone: "bg-[#fef3c7] text-[#92400e]" },
  qualified: { label: "Qualified", tone: "bg-[#e0e7ff] text-[#3730a3]" },
  converted: { label: "Converted", tone: "bg-[#dcfce7] text-[#166534]" },
  archived:  { label: "Archived",  tone: "bg-[#f4f4f5] text-[#71717a]" },
};

const STATUS_FLOW = ["new", "contacted", "qualified", "converted", "archived"];

export default function Leads() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [focus, setFocus] = useState(null); // currently-open lead

  async function load() {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : "";
      const { data } = await api.get(`/leads${q}`);
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  if (user && !user.is_provider) return <Navigate to="/" replace />;

  async function patch(id, body) {
    try {
      const { data } = await api.patch(`/leads/${id}`, body);
      setRows((prev) => prev.map((r) => r.id === id ? data : r));
      if (focus?.id === id) setFocus(data);
      toast.success("Lead updated");
    } catch (e) { toast.error(formatError(e)); }
  }

  async function remove(id) {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/leads/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      if (focus?.id === id) setFocus(null);
      toast.success("Lead deleted");
    } catch (e) { toast.error(formatError(e)); }
  }

  const counts = STATUS_FLOW.reduce((acc, s) => {
    acc[s] = rows.filter((r) => r.status === s).length; return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="leads-page">
      <header className="flex flex-wrap items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter">LUROMA · Leads Inbox</h1>
          <p className="text-sm text-[#71717a] mt-1">
            Prospective clients who submitted the Get Started form on the landing page.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="leads-refresh"
          className="ml-auto border border-[#e4e4e7] px-3 py-2 text-sm font-medium flex items-center gap-2 hover:bg-[#fafafa]"
        >
          <ArrowsClockwise size={14} weight="bold" /> Refresh
        </button>
      </header>

      <div className="flex flex-wrap gap-2" data-testid="leads-filter-bar">
        <FilterChip label="All" value="" count={rows.length} active={filter === ""} onClick={() => setFilter("")} />
        {STATUS_FLOW.map((s) => (
          <FilterChip
            key={s}
            label={STATUS_META[s].label}
            value={s}
            count={counts[s]}
            active={filter === s}
            onClick={() => setFilter(s)}
            testId={`leads-filter-${s}`}
          />
        ))}
      </div>

      {loading ? (
        <div className="text-sm phizo-label">Loading leads…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-[#e4e4e7] p-12 text-center text-sm text-[#71717a]">
          No leads {filter ? `with status "${filter}"` : "yet"}. When prospective clients hit <b>Get Started</b> on the landing page, they'll show up here.
        </div>
      ) : (
        <div className="border border-[#e4e4e7] bg-white">
          <table className="w-full text-sm" data-testid="leads-table">
            <thead className="bg-[#fafafa] border-b border-[#e4e4e7]">
              <tr className="phizo-label text-[10px] text-[#71717a]">
                <th className="text-left px-4 py-2">Company</th>
                <th className="text-left px-4 py-2">Contact</th>
                <th className="text-left px-4 py-2">Industry</th>
                <th className="text-left px-4 py-2">Received</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((l) => (
                <tr
                  key={l.id}
                  className="border-b border-[#e4e4e7] last:border-b-0 hover:bg-[#fafafa] cursor-pointer"
                  onClick={() => setFocus(l)}
                  data-testid={`lead-row-${l.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold">{l.company_name}</div>
                    {l.description && (
                      <div className="text-[11px] text-[#71717a] line-clamp-1 max-w-[280px]">
                        {l.description}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">{l.contact_name}</div>
                    <div className="text-[11px] text-[#71717a] mono">{l.email}</div>
                  </td>
                  <td className="px-4 py-3 text-xs">{l.industry || <span className="text-[#d4d4d8]">—</span>}</td>
                  <td className="px-4 py-3 text-xs text-[#71717a] mono">
                    {new Date(l.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATUS_META[l.status]?.tone || ""}`}>
                      {STATUS_META[l.status]?.label || l.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      onClick={() => remove(l.id)}
                      className="text-[#a1a1aa] hover:text-[#dc2626]"
                      data-testid={`lead-delete-${l.id}`}
                      title="Delete lead"
                    >
                      <Trash size={14} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {focus && (
        <LeadDetail
          lead={focus}
          onClose={() => setFocus(null)}
          onChangeStatus={(s) => patch(focus.id, { status: s })}
          onSaveNotes={(notes) => patch(focus.id, { notes })}
        />
      )}
    </div>
  );
}

function FilterChip({ label, count, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border transition-colors ${
        active
          ? "bg-black text-white border-black"
          : "bg-white text-[#52525b] border-[#e4e4e7] hover:border-[#a1a1aa]"
      }`}
    >
      {label}
      <span className={`px-1.5 py-0.5 text-[10px] rounded ${active ? "bg-white/20" : "bg-[#f4f4f5] text-[#71717a]"}`}>
        {count}
      </span>
    </button>
  );
}

function LeadDetail({ lead, onClose, onChangeStatus, onSaveNotes }) {
  const [notes, setNotes] = useState(lead.notes || "");
  useEffect(() => { setNotes(lead.notes || ""); }, [lead.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="lead-detail">
      <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-start justify-between px-6 py-4 border-b border-[#e4e4e7]">
          <div>
            <div className="phizo-label text-[10px] text-[#a1a1aa]">Lead detail</div>
            <h2 className="font-heading text-2xl font-black tracking-tighter">{lead.company_name}</h2>
            <div className="text-xs text-[#71717a] mt-0.5">
              Submitted {new Date(lead.created_at).toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} className="p-1 text-[#71717a] hover:text-black" data-testid="lead-detail-close">
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InfoRow icon={Handshake} label="Contact" value={lead.contact_name} />
            <InfoRow icon={Envelope} label="Email" value={<a href={`mailto:${lead.email}`} className="mono underline">{lead.email}</a>} />
            <InfoRow icon={Phone} label="Phone" value={lead.phone ? <a href={`tel:${lead.phone}`} className="mono">{lead.phone}</a> : "—"} />
            <InfoRow icon={Buildings} label="Industry" value={lead.industry || "—"} />
          </div>

          {lead.description && (
            <div>
              <div className="phizo-label text-[10px] mb-1">What they need</div>
              <p className="text-sm text-[#09090b] leading-relaxed bg-[#fafafa] border border-[#e4e4e7] p-4">
                {lead.description}
              </p>
            </div>
          )}

          <div>
            <div className="phizo-label text-[10px] mb-2">Move status</div>
            <div className="flex flex-wrap gap-2" data-testid="lead-status-buttons">
              {STATUS_FLOW.map((s) => {
                const active = lead.status === s;
                return (
                  <button
                    key={s}
                    type="button"
                    data-testid={`lead-status-${s}`}
                    onClick={() => onChangeStatus(s)}
                    disabled={active}
                    className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider border ${
                      active
                        ? `${STATUS_META[s].tone} border-transparent`
                        : "bg-white text-[#52525b] border-[#e4e4e7] hover:border-black"
                    }`}
                  >
                    {STATUS_META[s].label}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="phizo-label text-[10px] mb-1">Internal notes</div>
            <textarea
              data-testid="lead-notes"
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full border border-[#e4e4e7] bg-white px-3 py-2 text-sm focus:border-black outline-none"
              placeholder="Meeting notes, next steps, blockers…"
            />
            <div className="flex justify-end mt-2">
              <button
                type="button"
                data-testid="lead-save-notes"
                onClick={() => onSaveNotes(notes)}
                className="bg-[#002FA7] text-white text-sm font-medium px-4 py-2 hover:bg-[#001f7a]"
              >
                Save notes
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="border border-[#e4e4e7] p-3">
      <div className="flex items-center gap-1.5 phizo-label text-[10px] mb-1">
        <Icon size={11} weight="bold" /> {label}
      </div>
      <div className="text-sm font-medium">{value}</div>
    </div>
  );
}
